import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Heading,
  Box,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';

import { AuditLog } from './types';

type TokenKind = 'indent' | 'key' | 'colon' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'text';

interface Token {
  kind: TokenKind;
  value: string;
}

const TOKEN_COLORS: Record<TokenKind, string> = {
  indent: '0000008C',
  key: '#0000008C',
  colon: '0000008C',
  string: '#007F66',
  number: '#1976d2',
  boolean: '#7b1fa2',
  null: '#B30426',
  punctuation: '#0000008C',
  text: '#0000008C',
};

const JsonWithLineNumbers = ({ data }: { data: any }) => {
  const jsonLines = JSON.stringify(data, null, 2).split('\n');
  
  const tokenizeJsonLine = (line: string): Token[] => {
    // Check if line contains a key-value pair
    const keyValueMatch = line.match(/^(\s*)(".*?")(\s*:\s*)(.*)$/);
    if (keyValueMatch) {
      const [, indent, key, colon, value] = keyValueMatch;
      
      const tokens: Token[] = [
        { kind: 'indent', value: indent },
        { kind: 'key', value: key },
        { kind: 'colon', value: colon },
      ];
      
      // Determine value token kind
      let valueKind: TokenKind = 'text';
      if (value.match(/^".*"[,]?$/)) {
        valueKind = 'string';
      } else if (value.match(/^-?\d+(\.\d+)?[,]?$/)) {
        valueKind = 'number';
      } else if (value.match(/^(true|false)[,]?$/)) {
        valueKind = 'boolean';
      } else if (value.match(/^null[,]?$/)) {
        valueKind = 'null';
      }
      
      tokens.push({ kind: valueKind, value });
      return tokens;
    }
    
    // For non-key-value lines (braces, brackets), treat as punctuation or text
    const isPunctuation = line.trim().match(/^[{}[\],]*$/);
    return [{ 
      kind: isPunctuation ? 'punctuation' : 'text', 
      value: line 
    }];
  };
  
  return (
    <Box display='flex' w='100%'>
      {/* Line numbers column */}
      <Box
        minW='2em'
        pr={3}
        fontSize='14px'
        fontFamily='"SF Mono", Monaco, Menlo, "Ubuntu Mono", monospace'
        color='#0000008C'
        lineHeight='1.5'
        textAlign='right'
        userSelect='none'
        flexShrink={0}
      >
        {jsonLines.map((_, index) => (
          <Box key={index} minHeight='1.5em'>
            {index + 1}
          </Box>
        ))}
      </Box>
      
      {/* JSON content column - scrollable */}
      <Box
        fontSize='14px'
        fontFamily='"SF Mono", Monaco, Menlo, "Ubuntu Mono", monospace'
        lineHeight='1.5'
        margin={0}
        whiteSpace='pre'
        overflowX='auto'
        minW={0}
        flex={1}
      >
        {jsonLines.map((line, lineIndex) => (
          <Box key={lineIndex} minHeight='1.5em'>
            {tokenizeJsonLine(line).map((token, tokenIndex) => (
              <Box
                as='span'
                key={tokenIndex}
                color={TOKEN_COLORS[token.kind]}
              >
                {token.value}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const AuditLogDetailsDrawer = ({
  data,
  isOpen,
  onClose,
}: {
  data?: AuditLog | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const toast = useToast();

  if (!data) return null;

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <Drawer
      placement='right'
      onClose={onClose}
      isOpen={isOpen}
      size='lg'
      blockScrollOnMount
      autoFocus={false}
    >
      <DrawerOverlay />
      <DrawerContent
        data-testid='audit-log-details-drawer'
        h='100vh'
        maxH='100vh'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        px={0}
      >
        <DrawerHeader
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexShrink={0}
          pt={4}
          pb={4}
          px={6}
        >
          <Heading
            as='h4'
            size='md'
            data-testid='audit-log-details-drawer-header'
          >
            Audit log details
          </Heading>
          <DrawerCloseButton />
        </DrawerHeader>
        <DrawerBody
          flex='1'
          overflowY='auto'
          py={0}
          px={0}
          sx={{
            '::-webkit-scrollbar': { width: 0, height: 0 },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          }}
        >    
          <Box position='relative'>
            <IconButton
              aria-label='Copy JSON'
              icon={<CopyIcon />}
              size='sm'
              variant='ghost'
              position='absolute'
              top={2}
              right={3}
              zIndex={10}
              bg='white'
              onClick={handleCopyJson}
            />
            <Box pl={6} pr={6} pt={4} pb={4}>
              <JsonWithLineNumbers data={data} />
            </Box>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
